import { useRouter } from 'expo-router'
import type { ReactNode } from 'react'

import { ActionSheet } from '@/src/elements/action'
import { Button, type TDefaultButtonProps } from '@/src/elements/button/default'
import { Icon } from '@/src/elements/media/Icon'
import { ICON } from '@/src/general/enums/icons'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import type { TConversationApi } from '../../api/types/messageApi.types'
import { useBlockConversationMutation } from '../../services/useBlockConversationMutation'
import { useMarkConversationAsUnreadMutation } from '../../services/useMarkConversationAsUnreadMutation'
import { useUnblockConversationMutation } from '../../services/useUnlockConversationMutation'

type TConversationOptionsProps = {
  conversation: TConversationApi
  openedConversationId: number | undefined
} & Partial<TDefaultButtonProps>

export const ConversationOptions = ({
  conversation,
  openedConversationId,
  ...otherProps
}: TConversationOptionsProps): ReactNode => {
  const { t } = useTranslate()
  const { navigate } = useRouter()
  const { routes } = useRoutes()

  const { mutate: block } = useBlockConversationMutation(conversation.id)
  const { mutate: unblock } = useUnblockConversationMutation(conversation.id)
  const { mutate: markAsUnread } = useMarkConversationAsUnreadMutation(conversation.id)

  return (
    <ActionSheet
      items={[
        {
          children: t('conversation.mark-as-unread'),
          startContent: <Icon name={ICON.EYE} />,
          onPress: () => {
            markAsUnread()
            if (openedConversationId === conversation.id) navigate(routes.messages())
          },
        },
        {
          children: conversation?.isBlocked ? t('conversation.unblock') : t('conversation.block'),
          startContent: (
            <Icon name={ICON.SLASH_CIRCLE} size="lg" className="native:items-center native:justify-center" />
          ),
          onPress: () => (conversation?.isBlocked ? unblock() : block()),
        },
      ]}
    >
      <Button
        isIconOnly
        startIcon={ICON.ELLIPSIS_VERTICAL}
        contrast="low"
        colors="default"
        variant="light"
        size="sm"
        onPress={(e) => {
          e.stopPropagation()
          e.preventDefault()
        }}
        {...otherProps}
      />
    </ActionSheet>
  )
}
